<template>
  <!--
    Una lista que todavía no ha llegado, con la forma que va a tener.

    En el teléfono es la ficha de lista del ERP —la que dibuja UiTable cuando
    la tabla no cabe—: el nombre arriba (con su avatar si la lista lo lleva y
    una segunda línea más flojita), debajo los pares rótulo/valor y al final
    el estado como píldora. En una caja ancha, las filas de una tabla con su
    cabecera. Lo decide el ANCHO DE SU CAJA (consulta de contenedor) y no el
    de la ventana, igual que UiTable: así no cambia de forma al llegar los
    datos.

    Va donde va la lista de verdad: dentro de `<UiCard flush>`. Si la lista
    es un `UiTable`, NO hace falta: su `:loading` ya dibuja estas filas.
  -->
  <div
    class="sk-list"
    :class="[`sk-list--${layout}`, { 'sk-list--plain': plain }]"
    :style="{ '--sk-cols': gridColumns }"
    role="status"
    aria-busy="true"
  >
    <span class="sr-only">{{ uiLabel('loading') }}</span>

    <div v-if="header" class="sk-list__head" aria-hidden="true">
      <span class="sk-list__cell sk-list__cell--main"><UiSkeleton height="0.7em" width="72px" /></span>
      <span v-for="detail in details" :key="detail" class="sk-list__cell">
        <UiSkeleton height="0.7em" :width="`${48 + (detail * 13) % 30}px`" />
      </span>
      <span v-if="chip" class="sk-list__cell"><UiSkeleton height="0.7em" width="44px" /></span>
    </div>

    <div v-for="row in rows" :key="row" class="sk-list__row" aria-hidden="true">
      <div class="sk-list__cell sk-list__cell--main">
        <UiSkeleton v-if="avatar" variant="circle" width="36px" height="36px" />
        <div class="sk-list__name">
          <UiSkeleton height="1em" :width="pick(TITLE, row)" />
          <UiSkeleton v-if="subtitle" height="0.75em" :width="pick(SUB, row)" />
        </div>
      </div>

      <div v-for="detail in details" :key="detail" class="sk-list__cell sk-list__detail">
        <span class="sk-list__label"><UiSkeleton height="0.7em" :width="pick(LABEL, detail)" /></span>
        <span class="sk-list__value"><UiSkeleton :width="pick(VALUE, row + detail)" /></span>
      </div>

      <div v-if="chip" class="sk-list__cell sk-list__detail">
        <span class="sk-list__label"><UiSkeleton height="0.7em" width="46%" /></span>
        <span class="sk-list__value"><UiSkeleton variant="chip" :width="row % 2 ? '68px' : '84px'" /></span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

import UiSkeleton from './UiSkeleton.vue'
import { uiLabel } from '../utils/labels'

const props = defineProps({
  // Cuántas filas. Las que se esperan, más o menos: la página no debe crecer
  // ni encoger de golpe cuando llegan.
  rows: { type: Number, default: 5 },
  // Los pares rótulo/valor de cada ficha del teléfono, que en una caja ancha
  // son las columnas que hay entre el nombre y el estado.
  details: { type: Number, default: 2 },
  // El círculo con las iniciales delante del nombre (clientes, personas).
  avatar: Boolean,
  // La segunda línea bajo el nombre: el código, el NIF, el correo.
  subtitle: { type: Boolean, default: true },
  // La píldora de estado al final de la ficha.
  chip: { type: Boolean, default: true },
  // La fila de títulos de columna. Solo se ve en la forma de tabla.
  header: { type: Boolean, default: true },
  // auto: fichas si la caja mide menos de 720 px, tabla si no.
  // cards | table: siempre esa forma (una lista de tarjetas que nunca es
  // tabla, como las notificaciones).
  layout: {
    type: String,
    default: 'auto',
    validator: value => ['auto', 'cards', 'table'].includes(value),
  },
  // Sin raya entre filas ni relleno lateral: para una lista que va dentro de
  // otra caja que ya pone los suyos.
  plain: Boolean,
})

const gridColumns = computed(() =>
  ['minmax(0, 2fr)', ...Array(props.details).fill('minmax(0, 1fr)'), ...(props.chip ? ['96px'] : [])].join(' '))

// Anchos desiguales y fijos por fila: barras idénticas en rejilla perfecta se
// leen como un fallo de pintado; desiguales, como texto que aún no ha llegado.
const TITLE = ['62%', '48%', '70%', '54%', '66%', '44%']
const SUB = ['34%', '26%', '40%', '30%']
const LABEL = ['52%', '64%', '46%', '58%']
const VALUE = ['58%', '76%', '44%', '66%', '52%']
const pick = (list, index) => list[index % list.length]
</script>

<style scoped>
.sk-list { container-type: inline-size; width: 100%; }

/* ── Tabla (caja ancha) ─────────────────────────────────────────────────────
   Las mismas medidas que UiTable: cabecera de `--space-3` en `surface-2`,
   filas de `--space-4` con su raya, relleno lateral de `--space-5`. */
.sk-list__head,
.sk-list__row {
  display: grid;
  grid-template-columns: var(--sk-cols);
  align-items: center;
  gap: var(--space-5);
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--color-border);
}
.sk-list__head {
  padding-block: var(--space-3);
  background: var(--color-surface-2);
  /* La altura de una línea de `th` (texto xs), para que no salte. */
  min-height: calc(var(--text-xs) * 1.55 + 2 * var(--space-3) + 1px);
}
.sk-list__row:last-child { border-bottom: none; }

.sk-list__cell { display: flex; align-items: center; min-width: 0; }
.sk-list__cell--main { gap: var(--space-3); }
/* El valor mide su porcentaje contra lo que deja el rótulo, no contra la
   celda entera: si no, en la ficha del teléfono se sale por la derecha. */
.sk-list__value { display: flex; flex: 1; min-width: 0; }
.sk-list__name { display: flex; flex-direction: column; gap: 6px; flex: 1; min-width: 0; }
/* El rótulo solo existe en la ficha: en la tabla lo dice la cabecera. */
.sk-list__label { display: none; }

/* ── Fichas (teléfono, o caja estrecha) ─────────────────────────────────────
   La ficha de UiTable: el nombre arriba a todo el ancho y en tamaño md,
   debajo cada dato con su rótulo en el 38 % de la izquierda. */
@container (max-width: 720px) {
  .sk-list--auto .sk-list__head { display: none; }
  .sk-list--auto .sk-list__row { display: flex; flex-direction: column; align-items: stretch; gap: var(--space-2); }
  .sk-list--auto .sk-list__cell--main { margin-bottom: var(--space-1); }
  .sk-list--auto .sk-list__name :deep(.skeleton:first-child) { height: 1.15em !important; }
  .sk-list--auto .sk-list__detail { gap: var(--space-3); }
  .sk-list--auto .sk-list__label { display: flex; flex: 0 0 38%; }
}
.sk-list--cards .sk-list__head { display: none; }
.sk-list--cards .sk-list__row { display: flex; flex-direction: column; align-items: stretch; gap: var(--space-2); }
.sk-list--cards .sk-list__cell--main { margin-bottom: var(--space-1); }
.sk-list--cards .sk-list__name :deep(.skeleton:first-child) { height: 1.15em !important; }
.sk-list--cards .sk-list__detail { gap: var(--space-3); }
.sk-list--cards .sk-list__label { display: flex; flex: 0 0 38%; }

.sk-list--plain .sk-list__row { padding-inline: 0; border-bottom: none; }
.sk-list--plain .sk-list__head { display: none; }
</style>
